<template>
  <div class="custom-carousel">
    <div class="carousel-container" ref="container">
      <div 
        class="carousel-track" 
        ref="track"
        :style="{ transform: `translateX(-${scrollPosition}px)` }"
        @touchstart="startDrag"
        @touchmove="onDrag"
        @touchend="endDrag"
      >
        <div 
          v-for="product in products" 
          :key="product.id" 
          class="carousel-item"
          @click="productClick(product)"
        >
          <v-card
            class="product-card"
            height="380"
          >
            <v-img
              :src="product.image"
              height="200"
              class="white--text align-end"
            >
              <v-card-title 
                v-if="!product.inStock" 
                class="pa-2 red darken-2 text-caption font-weight-bold"
                style="position: absolute; top: 10px; right: 10px; border-radius: 4px; width: auto"
              >
                Esgotado
              </v-card-title>
            </v-img>

            <v-card-title class="text-subtitle-1 font-weight-medium pb-1 pt-3">
              {{ product.name }}
            </v-card-title>

            <v-card-text class="pt-0">
              <div class="text-h6 font-weight-bold primary--text mb-2">
                R$ {{ product.price.toFixed(2).replace('.', ',') }}
              </div>
              <div class="text-body-2 text-truncate">
                {{ product.description }}
              </div>
            </v-card-text>

            <v-card-actions class="px-4">
              <v-btn
                color="primary"
                outlined
                block
                @click.stop="productClick(product)"
              >
                <v-icon left>mdi-eye</v-icon>
                Ver detalhes
              </v-btn>
            </v-card-actions>
          </v-card>
        </div>
      </div>
    </div>
    
    <div class="carousel-controls" v-if="showControls">
      <v-btn icon small @click="scroll('left')" :disabled="scrollPosition <= 0">
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <div class="carousel-dots">
        <div 
          v-for="(_, index) in pages" 
          :key="index"
          class="carousel-dot"
          :class="{ active: currentPage === index }"
          @click="goToPage(index)"
        ></div>
      </div>
      <v-btn icon small @click="scroll('right')" :disabled="scrollPosition >= maxScroll">
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProductCarousel',
  props: {
    products: {
      type: Array,
      default: () => []
    },
    showControls: {
      type: Boolean,
      default: true
    },
    itemsPerPage: {
      type: Number,
      default: 3
    }
  },
  data() {
    return {
      scrollPosition: 0,
      containerWidth: 0,
      itemWidth: 0,
      maxScroll: 0,
      currentPage: 0,
      dragStart: null,
      dragPosition: null,
      isDragging: false
    };
  },
  computed: {
    pages() {
      if (this.products.length === 0 || this.itemWidth === 0) return [0];
      return Array.from({ length: Math.ceil(this.products.length / this.itemsPerPage) });
    }
  },
  mounted() {
    this.initCarousel();
    window.addEventListener('resize', this.initCarousel);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.initCarousel);
  },
  methods: {
    initCarousel() {
      this.$nextTick(() => {
        this.containerWidth = this.$refs.container.clientWidth;
        const items = this.$refs.track.querySelectorAll('.carousel-item');
        if (items.length > 0) {
          this.itemWidth = items[0].offsetWidth;
          this.maxScroll = Math.max(0, (this.products.length * this.itemWidth) - this.containerWidth);
          this.scrollToPage(this.currentPage);
        }
      });
    },
    productClick(product) {
      this.$emit('product-click', product);
    },
    scroll(direction) {
      if (direction === 'left') {
        this.currentPage = Math.max(0, this.currentPage - 1);
      } else {
        this.currentPage = Math.min(this.pages.length - 1, this.currentPage + 1);
      }
      this.scrollToPage(this.currentPage);
    },
    goToPage(pageIndex) {
      this.currentPage = pageIndex;
      this.scrollToPage(pageIndex);
    },
    scrollToPage(pageIndex) {
      this.scrollPosition = Math.min(this.maxScroll, pageIndex * this.itemsPerPage * this.itemWidth);
    },
    startDrag(e) {
      this.isDragging = true;
      this.dragStart = e.touches[0].clientX;
      this.dragPosition = this.scrollPosition;
    },
    onDrag(e) {
      if (!this.isDragging) return;
      const currentPosition = e.touches[0].clientX;
      const diff = this.dragStart - currentPosition;
      let newPosition = this.dragPosition + diff;
      
      // Limitar o scroll dentro dos limites
      newPosition = Math.max(0, Math.min(this.maxScroll, newPosition));
      this.scrollPosition = newPosition;
      
      // Prevenir o comportamento padrão de rolagem da página
      e.preventDefault();
    },
    endDrag() {
      this.isDragging = false;
      // Atualiza a página atual com base na posição final do scroll
      this.currentPage = Math.round(this.scrollPosition / (this.itemsPerPage * this.itemWidth));
      this.scrollToPage(this.currentPage);
    }
  }
};
</script>

<style scoped>
.custom-carousel {
  width: 100%;
  position: relative;
  padding: 16px 0;
}

.carousel-container {
  width: 100%;
  overflow: hidden;
}

.carousel-track {
  display: flex;
  transition: transform 0.3s ease;
}

.carousel-item {
  flex: 0 0 auto;
  width: 100%;
  padding: 0 8px;
  box-sizing: border-box;
}

.product-card {
  width: 100%;
  margin: 0;
}

.carousel-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
}

.carousel-dots {
  display: flex;
  justify-content: center;
  margin: 0 16px;
}

.carousel-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #e0e0e0;
  margin: 0 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.carousel-dot.active {
  background-color: #1976d2; /* Cor primária do Vuetify */
}

@media (min-width: 600px) {
  .carousel-item {
    width: 50%;
  }
}

@media (min-width: 960px) {
  .carousel-item {
    width: 33.33%;
  }
}

@media (min-width: 1264px) {
  .carousel-item {
    width: 25%;
  }
}
</style>